import React, { useState, useEffect } from 'react';
import { chatService } from '../../services/chat';
import {
  InterviewType,
  ExperienceLevel,
  StartInterviewRequest,
  ChatConfig,
} from '../../types/chat';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';

interface StartInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartInterview: (session: any) => void;
}

export const StartInterviewModal: React.FC<StartInterviewModalProps> = ({
  isOpen,
  onClose,
  onStartInterview,
}) => {
  const [config, setConfig] = useState<ChatConfig | null>(null);
  const [formData, setFormData] = useState<StartInterviewRequest>({
    interviewType: InterviewType.General,
    experienceLevel: ExperienceLevel.MidLevel,
    jobRole: '',
    companyName: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    try {
      const chatConfig = await chatService.getChatConfig();
      setConfig(chatConfig);

      if (chatConfig?.interviewTypes?.length > 0 && chatConfig?.experienceLevels?.length > 0) {
        const firstInterviewType = chatConfig.interviewTypes[0].type;
        const firstExperienceLevel = chatConfig.experienceLevels[0].level;

        setFormData((prev) => ({
          ...prev,
          interviewType: firstInterviewType,
          experienceLevel: firstExperienceLevel,
        }));
      }
    } catch (error) {
      console.error('Error loading chat config:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (formData.interviewType === null || formData.interviewType === undefined) {
        console.error('Invalid interview type:', formData.interviewType);
        return;
      }

      if (formData.experienceLevel === null || formData.experienceLevel === undefined) {
        console.error('Invalid experience level:', formData.experienceLevel);
        return;
      }

      const requestData: StartInterviewRequest = {
        interviewType: formData.interviewType,
        experienceLevel: formData.experienceLevel,
        jobRole: formData.jobRole?.trim() || undefined,
        companyName: formData.companyName?.trim() || undefined,
      };

      const session = await chatService.startInterview(requestData);
      onStartInterview(session);
      onClose();
    } catch (error) {
      console.error('Error starting interview:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const interviewTypeHint = config?.interviewTypes.find(
    (t) => String(t.type) === String(formData.interviewType),
  )?.description;
  const experienceHint = config?.experienceLevels.find(
    (l) => String(l.level) === String(formData.experienceLevel),
  )?.description;

  return (
    <Modal open={isOpen} onClose={onClose} size="md">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold leading-6 text-stitch-fg">
          Start New Interview Session
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-stitch-md p-1 text-stitch-muted transition hover:bg-stitch-surface-2 hover:text-stitch-fg"
          aria-label="Close"
        >
          <span className="material-symbols-outlined text-2xl">close</span>
        </button>
      </div>

      {!config ? (
        <div className="py-8 text-center text-stitch-fg-soft">
          <p>Loading configuration...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Interview Type"
            value={String(formData.interviewType)}
            onChange={(e) => {
              setFormData({ ...formData, interviewType: e.target.value as InterviewType });
            }}
            hint={interviewTypeHint}
          >
            {config.interviewTypes.map((type, idx) => (
              <option key={`type-${idx}`} value={String(type.type)}>
                {type.name}
              </option>
            ))}
          </Select>

          <Select
            label="Experience Level"
            value={String(formData.experienceLevel)}
            onChange={(e) => {
              setFormData({ ...formData, experienceLevel: e.target.value as ExperienceLevel });
            }}
            hint={experienceHint}
          >
            {config.experienceLevels.map((level, idx) => (
              <option key={`level-${idx}`} value={String(level.level)}>
                {level.name}
              </option>
            ))}
          </Select>

          <Input
            label="Job Role (Optional)"
            type="text"
            value={formData.jobRole || ''}
            onChange={(e) => setFormData({ ...formData, jobRole: e.target.value })}
            placeholder="e.g., Senior Software Engineer, Product Manager"
          />

          <Input
            label="Company Name (Optional)"
            type="text"
            value={formData.companyName || ''}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            placeholder="e.g., Google, Microsoft, Startup"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Starting...' : 'Start Interview'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
